import { createRouter, createWebHistory, type Router, type RouteRecordRaw } from 'vue-router';

import { webAuth, type WebAuth } from '../auth/web-auth.js';
import AdminLayout from '../layouts/AdminLayout.vue';
import ClientLayout from '../layouts/ClientLayout.vue';
import AdminApp from '../pages/admin/AdminApp.vue';
import AccessStatePage from '../pages/auth/AccessStatePage.vue';
import CallbackPage from '../pages/auth/CallbackPage.vue';
import LoginPage from '../pages/auth/LoginPage.vue';
import ClientMapPage from '../pages/client/ClientMapPage.vue';

/**
 * Separação obrigatória entre área do cliente e área administrativa (F19).
 *
 * A separação que importa é a da API: a rota do navegador não autoriza nada, e
 * o cliente não ganha permissão de escrita por conseguir abrir uma URL. Desde
 * WA01 as duas áreas exigem sessão da plataforma central; `/admin` exige admin
 * global. O guard só decide NAVEGAÇÃO: quem autoriza é a API a cada request.
 *
 * `/admin` NÃO aparece na navegação pública — e esconder a rota nunca foi
 * controle de acesso: quem recusa é o servidor.
 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/cliente',
  },
  { path: '/login', name: 'login', component: LoginPage },
  { path: '/auth/callback', name: 'auth-callback', component: CallbackPage },
  {
    path: '/pendente',
    name: 'pending',
    component: AccessStatePage,
    props: (route) => ({ state: route.query.motivo === 'conflito' ? 'conflict' : 'pending' }),
  },
  { path: '/suspenso', name: 'suspended', component: AccessStatePage, props: { state: 'suspended' } },
  {
    path: '/cliente',
    component: ClientLayout,
    meta: { area: 'client', requiresAccess: 'client' },
    children: [
      {
        // M03-R3: a entrada do cliente é a tela única com mapa e modais.
        path: '',
        name: 'client-home',
        component: ClientMapPage,
        props: (route) => ({ openHistory: route.query.historico === '1' }),
      },
      {
        // Favoritos antigos da página técnica abrem a tela nova com o
        // histórico de leituras em modal.
        path: 'leituras',
        redirect: { name: 'client-home', query: { historico: '1' } },
      },
    ],
  },
  {
    path: '/admin',
    component: AdminLayout,
    meta: { area: 'admin', requiresAccess: 'admin' },
    children: [
      {
        path: '',
        name: 'admin-home',
        component: AdminApp,
      },
    ],
  },
];

const STATE_ROUTE = {
  pending: '/pendente',
  suspended: '/suspenso',
  conflict: { path: '/pendente', query: { motivo: 'conflito' } },
} as const;

/**
 * Nenhuma tela protegida monta antes de `/v1/access/me`: sem sessão vai ao
 * portal; pendente/suspenso vão para a tela de estado; `/admin` só para admin
 * global (os demais voltam para `/cliente`).
 */
export function installAccessGuard(router: Router, auth: () => WebAuth = webAuth): void {
  router.beforeEach(async (to) => {
    const required = to.matched.find((record) => record.meta.requiresAccess !== undefined)?.meta.requiresAccess;
    if (required === undefined) return true;
    const access = await auth().access();
    if (access === null) return { path: '/login', query: { returnTo: to.fullPath } };
    if (access.status !== 'active') return STATE_ROUTE[access.status];
    if (required === 'admin' && !access.isPlatformAdmin) return '/cliente';
    return true;
  });
}

export function createAppRouter() {
  const router = createRouter({ history: createWebHistory(), routes });
  installAccessGuard(router);
  return router;
}
